/* eslint-disable no-underscore-dangle */
/**
 * WHERE THIS RECEIPT'S COST GOES — the one block on the approve screen that
 * turns a decision into a confirmation.
 *
 * It opens on the answer: the cost bucket the API derived from the receipt's
 * VEHICLE (`AU-AA-670 Parkolás` for a parking receipt on AU-AA-670), or, when
 * that vehicle has no bucket (no third-party vehicle has one), the operator
 * on the trip, so the cost is filed under the operator that paid it.
 * Absorbed is always offered and is not the default. Until 2026-11-02 it was
 * the default for a third party; why that changed is in
 * `Utils/expenseCostDestination`. The person reads one sentence saying what
 * will happen and presses Approve; the decision and the line are written
 * together by the API, so an approved receipt cannot end up without the line
 * that carries its cost.
 *
 * The one thing it owns beyond its choice is the operator list, and only when
 * it is asked for: "Search all operators…" is the fallback for a receipt
 * nothing auto-matched, and it fetches the list the first time it is opened.
 * The rows around it stay presentational.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import OperatorService from '../../Services/OperatorService';
import {
  ABSORBED,
  SEARCH,
  costDestinationOptions,
  describeExpenseConsequence,
  orderLineFor,
  preselectedDestination,
  settlementWarningFor,
} from '../../Utils/expenseCostDestination';

/** How many matches the search offers at once — a picker, not an export. */
export const SEARCH_RESULT_LIMIT = 25;

const operatorId = (operator) => String(operator?._id || operator?.Id || operator?.id || '');

const matches = (operator, term) => operatorId(operator)
  && String(operator?.name || '').toLowerCase().includes(term);

function ReceiptCostDestination({ expense, onChange, disabled = false }) {
  const [destination, setDestination] = useState(() => preselectedDestination(expense));
  const [searched, setSearched] = useState(null);
  const [searching, setSearching] = useState(false);
  const [term, setTerm] = useState('');
  const [operators, setOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [operatorsError, setOperatorsError] = useState('');

  const options = useMemo(() => {
    const base = costDestinationOptions(expense);
    return searched
      ? [{ value: searched.id, label: `${searched.name} · found by search`, group: 'searched' }, ...base]
      : base;
  }, [expense, searched]);

  // The chosen destination travels up as the `order_line` the API takes, plus
  // whether it is settled enough to press Approve on. Picking "Search…" and
  // not choosing anybody is not an answer.
  useEffect(() => {
    onChange?.({
      destination,
      orderLine: orderLineFor(destination),
      ready: destination !== SEARCH,
    });
  }, [destination, onChange]);

  const openSearch = async () => {
    setSearching(true);
    if (operators.length || loadingOperators) return;
    setLoadingOperators(true);
    setOperatorsError('');
    try {
      const response = await OperatorService.fetchOperators();
      setOperators(response?.data?.data || []);
    } catch {
      setOperatorsError('Could not load the operator list — try again.');
    } finally {
      setLoadingOperators(false);
    }
  };

  const handleDestination = (event) => {
    const { value } = event.target;
    if (value === SEARCH) {
      setDestination(SEARCH);
      openSearch();
      return;
    }
    setDestination(value);
  };

  const handleSearchPick = (event) => {
    const { value } = event.target;
    const picked = operators.find((operator) => operatorId(operator) === value);
    if (!picked) {
      setSearched(null);
      setDestination(SEARCH);
      return;
    }
    setSearched({ id: value, name: picked.name || value });
    setDestination(value);
  };

  const results = useMemo(() => {
    const needle = term.trim().toLowerCase();
    const pool = needle ? operators.filter((operator) => matches(operator, needle)) : operators;
    return pool.slice(0, SEARCH_RESULT_LIMIT);
  }, [operators, term]);

  const consequence = describeExpenseConsequence(expense, destination, searched);
  const warning = settlementWarningFor(expense, destination);

  return (
    <div className="mt-2" data-testid="receipt-cost-destination">
      <Form.Label className="small text-muted mb-1 d-block" htmlFor={`cost-destination-${expense._id}`}>
        Where does this cost go?
      </Form.Label>
      <Form.Select
        size="sm"
        id={`cost-destination-${expense._id}`}
        aria-label="Where does this cost go?"
        value={destination}
        onChange={handleDestination}
        disabled={disabled}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Form.Select>

      {searching && (
        <div className="mt-2" data-testid="receipt-cost-search">
          <Form.Control
            size="sm"
            type="search"
            placeholder="Search operators by name"
            aria-label="Search operators by name"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            disabled={disabled}
          />
          {loadingOperators && <Spinner animation="border" size="sm" className="mt-2" />}
          {operatorsError && (
            <div className="small text-danger mt-1">{operatorsError}</div>
          )}
          {!loadingOperators && !operatorsError && (
            <Form.Select
              size="sm"
              className="mt-2"
              aria-label="Operator found by search"
              value={searched?.id || ''}
              onChange={handleSearchPick}
              disabled={disabled}
            >
              <option value="">— pick an operator —</option>
              {results.map((operator) => (
                <option key={operatorId(operator)} value={operatorId(operator)}>
                  {operator.name}
                </option>
              ))}
            </Form.Select>
          )}
          <div className="small text-muted mt-1">
            An operator that is not on this trip is filed as a Cost, not as crew.
          </div>
        </div>
      )}

      {consequence && (
        <div
          className={`small mt-2 ${destination === ABSORBED ? 'text-muted' : 'fw-semibold'}`}
          data-testid="receipt-cost-consequence"
        >
          <i className="fa fa-arrow-right me-1" />
          {consequence}
        </div>
      )}

      {warning && (
        <div className="small text-danger mt-1" data-testid="receipt-cost-warning">{warning}</div>
      )}
    </div>
  );
}

export default ReceiptCostDestination;
